import { ComponentFactoryResolver, Injector, Injectable } from '@angular/core';
import { SatPopoverHorizontalAlign, SatPopoverVerticalAlign } from '@ncstate/sat-popover';

import { SgClickConfirmPlugin, SgClickConfirmPluginContext } from '../../../../types';
import { SgConfirmClickRef } from '../../../../confirm-click-ref';
import { SgClickConfirmPopoverContainer } from './container.component';

declare module '../../../../types' {
  interface SgClickConfirmPlugins {
    popover: SgClickConfirmPopoverPlugin;
  }

  interface SgClickConfirmOptions {
    /** POPOVER: Alignment of the popover on the horizontal axis. */
    horizontalAlign?: SatPopoverHorizontalAlign;

    /** POPOVER: Alignment of the popover on the vertical axis. */
    verticalAlign?: SatPopoverVerticalAlign;

    /** POPOVER: Whether the popover always opens with the specified alignment. */
    forceAlignment?: boolean;

    /**
     * POPOVER: Whether the popover's alignment is locked after opening. This prevents the popover
     * from changing its alignement when scrolling or changing the size of the viewport.
     */
    lockAlignment?: boolean;
  }
}

@Injectable()
export class SgClickConfirmPopoverPlugin implements SgClickConfirmPlugin {
  readonly name = 'popover' as 'popover';

  open(event: MouseEvent, ctx: SgClickConfirmPluginContext): SgConfirmClickRef {
    const sgConfirmClickRef = SgConfirmClickRef.create(this.name, ctx);
    const injector = Injector.create({
      providers: [{ provide: SgConfirmClickRef, useValue: sgConfirmClickRef }],
      parent: ctx.injector || ctx.vcRef.injector,
    });

    const cfr = injector.get<ComponentFactoryResolver>(<any>ComponentFactoryResolver);
    const cmpFactory = cfr.resolveComponentFactory<SgClickConfirmPopoverContainer>(SgClickConfirmPopoverContainer);

    const popoverCmpRef = ctx.vcRef.createComponent<SgClickConfirmPopoverContainer>(cmpFactory, 0, injector);
    popoverCmpRef.instance.component = ctx.component;
    popoverCmpRef.instance.injector = injector;
    popoverCmpRef.changeDetectorRef.markForCheck();
    popoverCmpRef.changeDetectorRef.detectChanges();

    // set the popover ref and copy previously set values.
    const popover = popoverCmpRef.instance.satPopover;
    if (ctx.options) {
      [
        'backdropClass',
        'hasBackdrop',
        'autoFocus',
        'horizontalAlign',
        'verticalAlign',
        'forceAlignment',
        'lockAlignment',
      ].forEach((k) => {
        if (ctx.options.hasOwnProperty(k)) {
          popover[k] = ctx.options[k];
        }
      });

      if (ctx.options.hasOwnProperty('scrollStrategy')) {
        popover.scrollStrategy = ctx.options.scrollStrategy;
      }
      if (ctx.options.hasOwnProperty('disableClose')) {
        popover.interactiveClose = !ctx.options.disableClose;
      }
    }

    popover.anchor = ctx.elRef;
    popover.realign();

    const unsub = sgConfirmClickRef.closed.subscribe((value) => {
      unsub.unsubscribe();
      popover.close(value);
      ctx.vcRef.clear();
      ctx.confirm(value);
    });
    popover.toggle();
    return sgConfirmClickRef;
  }
}
